import { useState, useEffect } from 'react';
import {
  Loader2,
  Grid3X3,
  CheckCircle,
  Link as LinkIcon,
  Key,
  Plus,
  Trash2,
  Upload,
  RefreshCw,
  Package,
  Database,
} from 'lucide-react';

import { App, AppCategory, Connection, SupabaseFeature, EXCLUDED_SUPABASE_SECRETS } from '@optidev/shared-types';

import { APP_CATEGORIES } from '@/components/apps/app-categories';
import { CustomThumbnailUpload } from '@/components/apps/custom-thumbnail-upload';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import toast from 'react-hot-toast';

import { useAuth } from '@/contexts/AuthContext';
import { appsApi } from '@/lib/api/apps';
import { connectionsApi } from '@/lib/api/connections';
import { deploymentsApi } from '@/lib/api/deployments';
import { projectApi as supabaseProjectApi } from '@/lib/api/supabase/project.api';
import { secretsApi as supabaseSecretsApi } from '@/lib/api/supabase/secrets.api';
import { Deployment } from '@/types/deployment';

// Available Supabase features
const SUPABASE_FEATURES: { value: SupabaseFeature; label: string }[] = [
  { value: 'database', label: 'Database' },
  { value: 'auth', label: 'Auth' },
  { value: 'storage', label: 'Storage' },
  { value: 'edge-functions', label: 'Edge Functions' },
];

// App category options - sourced from shared config
const CATEGORY_OPTIONS = APP_CATEGORIES;

interface SubmitAppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  existingApp?: App | null;
  onSuccess: () => void;
}

interface ConnectionRequirementInput {
  key: string;
  connectionId: string;  // Publisher's actual connection ID
  connectorId: string;
  label: string;
  description: string;
  required: boolean;
}

interface EnvVariableInput {
  key: string;
  label: string;
  description: string;
  required: boolean;
  masked: boolean;
}

/**
 * Constructs the thumbnail URL from deployment data.
 * Thumbnail URL format: https://{slug}-b{buildNumber}.{bucket}.{domain}/thumbnail.png
 */
const constructThumbnailUrl = (deployment: Deployment): string | null => {
  if (!deployment.publicUrl || !deployment.r2Prefix) return null;

  try {
    // Extract slug from publicUrl: "https://echo-eagle-86740.fly-sandbox4.optidev.cc"
    const url = new URL(deployment.publicUrl);
    const hostParts = url.hostname.split('.');
    if (hostParts.length < 3) return null;

    const slug = hostParts[0]; // "echo-eagle-86740"
    const bucket = hostParts[1]; // "fly-sandbox4"
    const domain = hostParts.slice(2).join('.'); // "optidev.cc"

    // Extract buildNumber from r2Prefix: "projects/echo-eagle-86740/deployments/42/"
    const prefixParts = deployment.r2Prefix.split('/').filter(Boolean);
    const deploymentsIndex = prefixParts.indexOf('deployments');
    if (deploymentsIndex === -1 || deploymentsIndex + 1 >= prefixParts.length) return null;
    const buildNumber = prefixParts[deploymentsIndex + 1]; // "42"

    return `https://${slug}-b${buildNumber}.${bucket}.${domain}/thumbnail.png`;
  } catch {
    return null;
  }
};

/**
 * Formats a KEY_NAME to "Key Name" for display as label.
 * Example: "OPENAI_API_KEY" -> "Openai Api Key"
 */
const formatKeyToLabel = (key: string): string => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export function SubmitAppModal({
  open,
  onOpenChange,
  projectId,
  projectName,
  existingApp,
  onSuccess,
}: SubmitAppModalProps) {
  const { getIdToken } = useAuth();

  // Form state
  const [name, setName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<AppCategory[]>([]);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [customThumbnailUrl, setCustomThumbnailUrl] = useState<string | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState('');
  const [author, setAuthor] = useState('');

  // Connection requirements
  const [connectionReqs, setConnectionReqs] = useState<ConnectionRequirementInput[]>([]);

  // Environment variables
  const [envVariables, setEnvVariables] = useState<EnvVariableInput[]>([]);

  // Supabase (OptiDev Cloud) requirements
  const [hasSupabase, setHasSupabase] = useState(false);
  const [supabaseRequired, setSupabaseRequired] = useState(false);
  const [supabaseFeatures, setSupabaseFeatures] = useState<SupabaseFeature[]>([]);
  const [supabaseAutoActivate, setSupabaseAutoActivate] = useState(true);
  const [supabaseSecrets, setSupabaseSecrets] = useState<EnvVariableInput[]>([]);
  const [loadingSupabase, setLoadingSupabase] = useState(false);
  const [loadingSupabaseSecrets, setLoadingSupabaseSecrets] = useState(false);

  // Data state - user's existing connections
  const [userConnections, setUserConnections] = useState<Connection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [loadingDeployment, setLoadingDeployment] = useState(false);
  const [loadingPackageInfo, setLoadingPackageInfo] = useState(false);

  // Auto-detected project info from package.json
  const [packageInfo, setPackageInfo] = useState<{
    metadata: { version?: string; author?: string; framework?: string };
    config: { buildCommand?: string; startCommand?: string; installCommand?: string };
  } | null>(null);

  // Existing tags from other apps (for suggestions)
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const isResubmit = !!existingApp;

  // Load user's connections and existing tags when modal opens
  useEffect(() => {
    if (open) {
      loadUserConnections();
      loadExistingTags();
      // Initialize form with existing app data or project name
      if (existingApp) {
        setName(existingApp.name);
        setSelectedCategories(existingApp.categories as AppCategory[] || []);
        setDescription(existingApp.description || '');
        setTags(existingApp.tags || []);
        setThumbnailUrl(existingApp.thumbnailUrl || '');
        setCustomThumbnailUrl(existingApp.customThumbnailUrl ?? undefined);
        setPreviewUrl(existingApp.previewUrl || '');
        setAuthor(existingApp.metadata?.author || '');
        setConnectionReqs(
          (existingApp.connections || []).map((c) => ({
            key: c.key,
            connectionId: c.connectionId,
            connectorId: c.connectorId,
            label: c.label,
            description: c.description || '',
            required: c.required,
          }))
        );
        setEnvVariables(
          (existingApp.config?.envVariables || []).map((e) => ({
            key: e.key,
            label: e.label,
            description: e.description || '',
            required: e.required,
            masked: e.masked || false,
          }))
        );
        // Load supabase secrets from existing app (filter out standard keys)
        setSupabaseSecrets(
          (existingApp.config?.supabase?.secrets || [])
            .filter((s) => !(EXCLUDED_SUPABASE_SECRETS as readonly string[]).includes(s.key))
            .map((s) => ({
              key: s.key,
              label: s.label,
              description: s.description || '',
              required: s.required,
              masked: true,
            }))
        );
      } else {
        setName(projectName);
        setSelectedCategories([]);
        setDescription('');
        setTags([]);
        setThumbnailUrl('');
        setCustomThumbnailUrl(undefined);
        setPreviewUrl('');
        setAuthor('');
        setConnectionReqs([]);
        setEnvVariables([]);
        setPackageInfo(null);
        setHasSupabase(false);
        setSupabaseRequired(false);
        setSupabaseFeatures([]);
        setSupabaseAutoActivate(true);
        setSupabaseSecrets([]);
        loadSupabaseStatus();
      }
      // Also load Supabase status for re-submits to restore saved data
      if (existingApp) {
        loadSupabaseStatus();
      }
      // Always load latest deployment info (for both new and re-submits)
      loadDeploymentInfo();
      // Always load package info (for both new and re-submits)
      loadPackageInfo();
    }
  }, [open, existingApp, projectName]);

  const loadUserConnections = async () => {
    try {
      setLoadingConnections(true);
      const token = await getIdToken();
      if (!token) return;

      const data = await connectionsApi.getAll(token);
      setUserConnections(data);
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Load existing tags from all apps for suggestions
  const loadExistingTags = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;

      const apps = await appsApi.getAll(token);
      // Extract unique tags from all apps
      const allTags = apps.flatMap((app) => app.tags || []);
      const uniqueTags = [...new Set(allTags)].sort();
      setExistingTags(uniqueTags);
    } catch (err) {
      console.error('Failed to load existing tags:', err);
    }
  };

  // Load deployment info to auto-populate preview URLs (for both new submissions and re-submits)
  const loadDeploymentInfo = async () => {
    try {
      setLoadingDeployment(true);
      const deployment = await deploymentsApi.getCurrentDeployment(projectId);

      if (deployment?.publicUrl) {
        setPreviewUrl(deployment.publicUrl);

        const thumbnailUrl = constructThumbnailUrl(deployment);
        if (thumbnailUrl) {
          setThumbnailUrl(thumbnailUrl);
        }
      }
    } catch (err) {
      // Deployment may not exist yet, that's OK - leave fields empty for manual entry
      console.log('No current deployment found for auto-populate');
    } finally {
      setLoadingDeployment(false);
    }
  };

  // Load project info (package.json) to auto-populate metadata/config
  // Works for both new submissions and re-submits (user can click Refresh)
  const loadPackageInfo = async () => {
    try {
      setLoadingPackageInfo(true);
      const token = await getIdToken();
      if (!token) return;

      const info = await appsApi.getProjectPackageInfo(token, projectId);
      setPackageInfo(info);
      // Auto-populate author from package.json if not already set
      if (info.metadata?.author && !author) {
        setAuthor(info.metadata.author);
      }
    } catch (err) {
      // Project info may not be available, that's OK
      console.log('Could not load project info:', err);
    } finally {
      setLoadingPackageInfo(false);
    }
  };

  // Load Supabase status to auto-detect if project uses OptiDev Cloud
  const loadSupabaseStatus = async () => {
    // For re-submits, use existing app data
    if (existingApp) {
      if (existingApp.config?.supabase) {
        setHasSupabase(true);
        setSupabaseRequired(existingApp.config.supabase.required);
        setSupabaseFeatures(existingApp.config.supabase.features || []);
        setSupabaseAutoActivate(existingApp.config.supabase.autoActivate ?? true);
      }
      return;
    }

    try {
      setLoadingSupabase(true);
      const token = await getIdToken();
      if (!token) return;

      const status = await supabaseProjectApi.getProjectStatus(token, projectId);

      if (status.isActivated) {
        setHasSupabase(true);
        setSupabaseRequired(true); // Default to required
        setSupabaseFeatures(['database']); // Default features
        setSupabaseAutoActivate(true);
      }
    } catch (err) {
      // Supabase not activated - that's OK
      console.log('Supabase not activated for project');
    } finally {
      setLoadingSupabase(false);
    }
  };

  // Load Supabase Edge Function secrets and auto-populate supabaseSecrets
  const loadSupabaseSecrets = async () => {
    // Only for new submissions with Supabase activated
    if (existingApp || !hasSupabase) return;

    try {
      setLoadingSupabaseSecrets(true);
      const token = await getIdToken();
      if (!token) return;

      const secrets = await supabaseSecretsApi.listSecrets(token, projectId);

      if (secrets.length > 0) {
        // Map Supabase secrets to secret inputs, filtering out standard keys
        const newSecrets: EnvVariableInput[] = secrets
          .filter((secret) => !(EXCLUDED_SUPABASE_SECRETS as readonly string[]).includes(secret.name))
          .map((secret) => ({
            key: secret.name,
            label: formatKeyToLabel(secret.name),
            description: '',
            required: true,
            masked: true, // Always masked since these are secrets
          }));
        setSupabaseSecrets(newSecrets);
      }
    } catch (err) {
      // Supabase secrets may not be accessible, that's OK
      console.log('Could not load Supabase secrets:', err);
    } finally {
      setLoadingSupabaseSecrets(false);
    }
  };

  // Trigger loading Supabase secrets when hasSupabase becomes true
  useEffect(() => {
    if (open && hasSupabase && !existingApp) {
      loadSupabaseSecrets();
    }
  }, [open, hasSupabase, existingApp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Build the DTO
      const dto = {
        name: name.trim(),
        categories: selectedCategories,
        description: description.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        thumbnailUrl: thumbnailUrl.trim() || undefined,
        customThumbnailUrl: customThumbnailUrl || undefined,
        previewUrl: previewUrl.trim() || undefined,
        connections: connectionReqs.length > 0
          ? connectionReqs.map((c) => ({
              key: c.key,
              connectionId: c.connectionId,
              connectorId: c.connectorId,
              label: c.label,
              description: c.description || undefined,
              required: c.required,
            }))
          : undefined,
        envVariables: envVariables.length > 0
          ? envVariables.map((e) => ({
              key: e.key,
              label: e.label,
              description: e.description || undefined,
              required: e.required,
              masked: e.masked,
            }))
          : undefined,
        // Include Supabase requirements if project uses OptiDev Cloud
        supabase: hasSupabase && supabaseRequired
          ? {
              required: true,
              features: supabaseFeatures,
              autoActivate: supabaseAutoActivate,
              secrets: supabaseSecrets.length > 0
                ? supabaseSecrets.map((s) => ({
                    key: s.key,
                    label: s.label,
                    description: s.description || undefined,
                    required: s.required,
                  }))
                : undefined,
            }
          : undefined,
        // Include metadata (user-editable author + auto-detected framework/version)
        metadata: {
          ...(packageInfo?.metadata || {}),
          ...(author.trim() ? { author: author.trim() } : {}),
        },
        // Include config (build/start/install commands from packageInfo or existingApp)
        config: packageInfo?.config || existingApp?.config ? {
          ...(packageInfo?.config?.buildCommand && { buildCommand: packageInfo.config.buildCommand }),
          ...(packageInfo?.config?.startCommand && { startCommand: packageInfo.config.startCommand }),
          ...(packageInfo?.config?.installCommand && { installCommand: packageInfo.config.installCommand }),
          // Fallback to existing app config if no packageInfo (for re-submits)
          ...(!packageInfo?.config && existingApp?.config?.buildCommand && { buildCommand: existingApp.config.buildCommand }),
          ...(!packageInfo?.config && existingApp?.config?.startCommand && { startCommand: existingApp.config.startCommand }),
          ...(!packageInfo?.config && existingApp?.config?.installCommand && { installCommand: existingApp.config.installCommand }),
        } : undefined,
      };

      // Debug: Log the DTO being sent
      console.log('[SubmitAppModal] Submitting DTO:', JSON.stringify(dto, null, 2));

      if (isResubmit && existingApp) {
        await appsApi.resubmit(token, existingApp.id, dto);
      } else {
        await appsApi.submit(token, projectId, dto);
      }

      setIsLoading(false);
      setIsSuccess(true);

      // Show success briefly then close
      setTimeout(() => {
        onSuccess();
        resetAndClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('Submit to App Library failed:', err);
      const axiosError = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.response?.data?.error ||
        axiosError?.message ||
        'Failed to submit app';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const resetAndClose = () => {
    setName('');
    setSelectedCategories([]);
    setDescription('');
    setTags([]);
    setTagInput('');
    setThumbnailUrl('');
    setCustomThumbnailUrl(undefined);
    setPreviewUrl('');
    setAuthor('');
    setConnectionReqs([]);
    setEnvVariables([]);
    setPackageInfo(null);
    setHasSupabase(false);
    setSupabaseRequired(false);
    setSupabaseFeatures([]);
    setSupabaseAutoActivate(true);
    setSupabaseSecrets([]);
    setIsSuccess(false);
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isLoading && !isSuccess) {
      resetAndClose();
    }
  };

  // Tag management
  const addTag = (tagToAdd?: string) => {
    const tag = (tagToAdd || tagInput).trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Escape') {
      setShowTagSuggestions(false);
    }
  };

  // Filter suggestions based on input
  const filteredTagSuggestions = existingTags.filter(
    (tag) =>
      tag.includes(tagInput.toLowerCase()) &&
      !tags.includes(tag)
  ).slice(0, 8); // Limit to 8 suggestions

  // Category toggle for multi-select
  const toggleCategory = (category: AppCategory) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        // Don't allow deselecting if it's the only one
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  // Connection requirement management
  const addConnectionReq = () => {
    setConnectionReqs([
      ...connectionReqs,
      {
        key: `conn-${Date.now()}`,
        connectionId: '',
        connectorId: '',
        label: '',
        description: '',
        required: true,
      },
    ]);
  };

  const updateConnectionReq = (index: number, updates: Partial<ConnectionRequirementInput>) => {
    const newConnections = [...connectionReqs];
    newConnections[index] = { ...newConnections[index], ...updates };
    setConnectionReqs(newConnections);
  };

  // When a connection is selected, auto-fill connectorId and label
  const selectConnection = (index: number, connectionId: string) => {
    const connection = userConnections.find((c) => c.id === connectionId);
    if (connection) {
      updateConnectionReq(index, {
        connectionId: connection.id,
        connectorId: connection.type || '',
        label: connection.name,
      });
    }
  };

  const removeConnectionReq = (index: number) => {
    setConnectionReqs(connectionReqs.filter((_, i) => i !== index));
  };

  // Environment variable management
  const addEnvVariable = () => {
    setEnvVariables([
      ...envVariables,
      {
        key: '',
        label: '',
        description: '',
        required: true,
        masked: false,
      },
    ]);
  };

  const updateEnvVariable = (index: number, updates: Partial<EnvVariableInput>) => {
    const newEnvVariables = [...envVariables];
    newEnvVariables[index] = { ...newEnvVariables[index], ...updates };
    setEnvVariables(newEnvVariables);
  };

  const removeEnvVariable = (index: number) => {
    setEnvVariables(envVariables.filter((_, i) => i !== index));
  };

  // Form validation
  const isFormValid = (): boolean => {
    if (!name.trim()) return false;
    if (!description.trim()) return false;
    if (selectedCategories.length === 0) return false;

    // Validate connection requirements
    for (const conn of connectionReqs) {
      if (!conn.connectionId || !conn.label) return false;
    }

    // Validate env variables
    for (const env of envVariables) {
      if (!env.key || !env.label) return false;
    }

    return true;
  };

  // Success screen
  if (isSuccess) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[450px]">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-lg font-medium">
              {isResubmit ? 'App Updated!' : 'App Submitted!'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isResubmit
                ? `${name} has been updated in the App Library`
                : `${name} is now available in the App Library`}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[85vh] flex flex-col p-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          // Only allow escape if not loading
          if (isLoading || isSuccess) e.preventDefault();
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5 text-purple-500" />
            {isResubmit ? 'Re-submit to App Library' : 'Submit to App Library'}
          </DialogTitle>
          <DialogDescription>
            {isResubmit
              ? 'Update your app in the library with the latest code and settings'
              : 'Share your app with others in the community'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* App Details Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">App Details</h4>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="My Awesome App"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>

            {/* Categories - Multi-select checkboxes */}
            <div className="space-y-2">
              <Label>Categories * (select at least one)</Label>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORY_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`cat-${opt.value}`}
                      checked={selectedCategories.includes(opt.value)}
                      onCheckedChange={() => toggleCategory(opt.value)}
                      disabled={isLoading}
                    />
                    <Label
                      htmlFor={`cat-${opt.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what your app does..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
                rows={3}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="relative">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Add a tag..."
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value);
                      setShowTagSuggestions(true);
                    }}
                    onFocus={() => setShowTagSuggestions(true)}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowTagSuggestions(false), 200);
                    }}
                    onKeyDown={handleTagKeyDown}
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => addTag()}
                    disabled={isLoading || !tagInput.trim()}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Tag suggestions dropdown */}
                {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                    <div className="p-1">
                      <p className="px-2 py-1 text-xs text-muted-foreground">Existing tags:</p>
                      {filteredTagSuggestions.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted cursor-pointer"
                          onClick={() => addTag(tag)}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => removeTag(tag)}
                    >
                      {tag}
                      <Trash2 className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Connection Requirements Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                <h4 className="text-sm font-medium">Connections Used</h4>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addConnectionReq}
                disabled={isLoading || loadingConnections || userConnections.length === 0}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Select your connections that this app uses. Users will need to provide their own connections of the same type.
            </p>

            {userConnections.length === 0 && !loadingConnections && (
              <p className="text-xs text-muted-foreground italic">
                No connections available. Create connections in your workspace first.
              </p>
            )}

            {connectionReqs.map((conn, index) => (
              <div
                key={conn.key}
                className="border rounded-lg p-3 space-y-3 bg-muted/30"
              >
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div className="space-y-1">
                      <Label className="text-xs">Select Connection *</Label>
                      <Select
                        value={conn.connectionId}
                        onValueChange={(value) => selectConnection(index, value)}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Select connection" />
                        </SelectTrigger>
                        <SelectContent>
                          {userConnections.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} ({c.type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label *</Label>
                      <Input
                        type="text"
                        placeholder="e.g., Stripe Account"
                        value={conn.label}
                        onChange={(e) =>
                          updateConnectionReq(index, { label: e.target.value })
                        }
                        disabled={isLoading}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeConnectionReq(index)}
                    disabled={isLoading}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    type="text"
                    placeholder="Help text for remixers..."
                    value={conn.description}
                    onChange={(e) =>
                      updateConnectionReq(index, { description: e.target.value })
                    }
                    disabled={isLoading}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`conn-required-${index}`}
                      checked={conn.required}
                      onCheckedChange={(checked) =>
                        updateConnectionReq(index, { required: !!checked })
                      }
                      disabled={isLoading}
                    />
                    <Label htmlFor={`conn-required-${index}`} className="text-xs">
                      Required
                    </Label>
                  </div>
                  {conn.connectionId && (
                    <span className="text-xs text-muted-foreground">
                      ID: {conn.connectionId.slice(0, 8)}...
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Environment Variables Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                <h4 className="text-sm font-medium">Environment Variables</h4>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addEnvVariable}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Define what config values remixers need to set
            </p>

            {envVariables.map((env, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 space-y-3 bg-muted/30"
              >
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div className="space-y-1">
                      <Label className="text-xs">Variable Key *</Label>
                      <Input
                        type="text"
                        placeholder="e.g., OPENAI_API_KEY"
                        value={env.key}
                        onChange={(e) =>
                          updateEnvVariable(index, {
                            key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
                          })
                        }
                        disabled={isLoading}
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label *</Label>
                      <Input
                        type="text"
                        placeholder="e.g., OpenAI API Key"
                        value={env.label}
                        onChange={(e) =>
                          updateEnvVariable(index, { label: e.target.value })
                        }
                        disabled={isLoading}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeEnvVariable(index)}
                    disabled={isLoading}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    type="text"
                    placeholder="Help text for remixers..."
                    value={env.description}
                    onChange={(e) =>
                      updateEnvVariable(index, { description: e.target.value })
                    }
                    disabled={isLoading}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`env-required-${index}`}
                      checked={env.required}
                      onCheckedChange={(checked) =>
                        updateEnvVariable(index, { required: !!checked })
                      }
                      disabled={isLoading}
                    />
                    <Label htmlFor={`env-required-${index}`} className="text-xs">
                      Required
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`env-masked-${index}`}
                      checked={env.masked}
                      onCheckedChange={(checked) =>
                        updateEnvVariable(index, { masked: !!checked })
                      }
                      disabled={isLoading}
                    />
                    <Label htmlFor={`env-masked-${index}`} className="text-xs">
                      Masked (password field)
                    </Label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* OptiDev Cloud (Supabase) Requirements Section - only show if project has Supabase */}
          {(hasSupabase || loadingSupabase) && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <h4 className="text-sm font-medium">OptiDev Cloud (Supabase)</h4>
                {loadingSupabase && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                This project uses OptiDev Cloud. Configure requirements for remixers.
              </p>

              <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="supabase-required"
                    checked={supabaseRequired}
                    onCheckedChange={(checked) => setSupabaseRequired(!!checked)}
                    disabled={isLoading}
                  />
                  <Label htmlFor="supabase-required" className="text-sm">
                    OptiDev Cloud required for this app
                  </Label>
                </div>

                {supabaseRequired && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs">Features used:</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {SUPABASE_FEATURES.map((feature) => (
                          <div key={feature.value} className="flex items-center gap-2">
                            <Checkbox
                              id={`supabase-${feature.value}`}
                              checked={supabaseFeatures.includes(feature.value)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSupabaseFeatures([...supabaseFeatures, feature.value]);
                                } else {
                                  setSupabaseFeatures(supabaseFeatures.filter((f) => f !== feature.value));
                                }
                              }}
                              disabled={isLoading}
                            />
                            <Label htmlFor={`supabase-${feature.value}`} className="text-xs">
                              {feature.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="supabase-auto-activate"
                        checked={supabaseAutoActivate}
                        onCheckedChange={(checked) => setSupabaseAutoActivate(!!checked)}
                        disabled={isLoading}
                      />
                      <Label htmlFor="supabase-auto-activate" className="text-xs">
                        Auto-activate for remixers (recommended)
                      </Label>
                    </div>

                    {/* Supabase Edge Function Secrets */}
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          Edge Function Secrets
                          {loadingSupabaseSecrets && (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                          )}
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSupabaseSecrets([
                            ...supabaseSecrets,
                            { key: '', label: '', description: '', required: true, masked: true },
                          ])}
                          disabled={isLoading}
                          className="h-6 text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Secrets remixers need to configure for Edge Functions.
                        <span className="text-amber-600"> Standard keys (SUPABASE_URL, etc.) are auto-populated.</span>
                      </p>

                      {supabaseSecrets.map((secret, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-background rounded p-2"
                        >
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input
                              type="text"
                              placeholder="SECRET_NAME"
                              value={secret.key}
                              onChange={(e) => {
                                const newKey = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_');

                                // Warn if trying to add a standard key
                                if ((EXCLUDED_SUPABASE_SECRETS as readonly string[]).includes(newKey)) {
                                  toast.error(`${newKey} is auto-populated by OptiDev Cloud and cannot be added manually`);
                                  return;
                                }

                                const newSecrets = [...supabaseSecrets];
                                newSecrets[index] = {
                                  ...newSecrets[index],
                                  key: newKey,
                                };
                                setSupabaseSecrets(newSecrets);
                              }}
                              disabled={isLoading}
                              className="h-7 text-xs font-mono"
                            />
                            <Input
                              type="text"
                              placeholder="Label"
                              value={secret.label}
                              onChange={(e) => {
                                const newSecrets = [...supabaseSecrets];
                                newSecrets[index] = { ...newSecrets[index], label: e.target.value };
                                setSupabaseSecrets(newSecrets);
                              }}
                              disabled={isLoading}
                              className="h-7 text-xs"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Checkbox
                              id={`supabase-secret-required-${index}`}
                              checked={secret.required}
                              onCheckedChange={(checked) => {
                                const newSecrets = [...supabaseSecrets];
                                newSecrets[index] = { ...newSecrets[index], required: !!checked };
                                setSupabaseSecrets(newSecrets);
                              }}
                              disabled={isLoading}
                            />
                            <Label htmlFor={`supabase-secret-required-${index}`} className="text-xs">
                              Req
                            </Label>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setSupabaseSecrets(supabaseSecrets.filter((_, i) => i !== index))}
                            disabled={isLoading}
                            className="h-6 w-6 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}

                      {supabaseSecrets.length === 0 && !loadingSupabaseSecrets && (
                        <p className="text-xs text-muted-foreground italic py-1">
                          No secrets detected. Add manually if needed.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Project Info Section - Auto-detected from package.json */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <h4 className="text-sm font-medium">Project Info</h4>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={loadPackageInfo}
                disabled={isLoading || loadingPackageInfo}
                className="h-7 text-xs"
              >
                {loadingPackageInfo ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                {loadingPackageInfo ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {existingApp
                ? 'Click Refresh to update from current project. This info helps remixers understand your app.'
                : 'Auto-detected from package.json. This info helps remixers understand your app.'}
            </p>

            {loadingPackageInfo ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (packageInfo || existingApp?.config) ? (
              <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Framework:</span>
                    <Badge variant="outline" className="ml-2">
                      {packageInfo?.metadata?.framework || existingApp?.metadata?.framework || 'Unknown'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Version:</span>
                    <span className="ml-2 font-mono">
                      {packageInfo?.metadata?.version || existingApp?.metadata?.version || '-'}
                    </span>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Install:</span>
                    <code className="bg-muted px-1.5 py-0.5 rounded font-mono">
                      {packageInfo?.config?.installCommand || existingApp?.config?.installCommand || 'npm install'}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start:</span>
                    <code className="bg-muted px-1.5 py-0.5 rounded font-mono">
                      {packageInfo?.config?.startCommand || existingApp?.config?.startCommand || '-'}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Build:</span>
                    <code className="bg-muted px-1.5 py-0.5 rounded font-mono">
                      {packageInfo?.config?.buildCommand || existingApp?.config?.buildCommand || '-'}
                    </code>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic py-2">
                Could not detect project info. Make sure the dev container is running.
              </p>
            )}

          </div>

          {/* Author field - always editable */}
          <div className="space-y-2">
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              placeholder="Your name or organization"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Who created this app (shown to remixers)
            </p>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Preview (Optional)</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={loadDeploymentInfo}
                disabled={isLoading || loadingDeployment}
                className="h-7 text-xs"
              >
                {loadingDeployment ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                {loadingDeployment ? 'Loading...' : 'Refresh from deployment'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              URLs are auto-populated from your latest deployment. You can override them manually.
            </p>

            <CustomThumbnailUpload
              value={customThumbnailUrl}
              onChange={setCustomThumbnailUrl}
              projectId={projectId}
              disabled={isLoading || loadingDeployment}
            />

            <div className="space-y-2">
              <Label htmlFor="previewUrl">Live Preview URL</Label>
              <Input
                id="previewUrl"
                type="url"
                placeholder="https://my-app.optidev.app"
                value={previewUrl}
                onChange={(e) => setPreviewUrl(e.target.value)}
                disabled={isLoading || loadingDeployment}
              />
            </div>
          </div>

          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid() || isLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isResubmit ? 'Updating...' : 'Submitting...'}
                </>
              ) : isResubmit ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Re-submit to Library
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Submit to Library
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
